import React, { useState } from 'react';
import { MoreVertical, Check, X, Clock, Star, RotateCcw, Eye, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';

interface BookRequest {
  id: string;
  book_id: string;
  title: string;
  author: string;
  cover_image: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled' | 'completed';
  created_at: string;
  owner_name?: string;
  owner_city?: string;
  requester_name?: string;
  requester_city?: string;
  requester_average_rating?: number;
  requester_total_ratings?: number;
  requester_trust_score?: number;
  can_rate?: boolean | number;
  rental_start_date?: string;
  rental_end_date?: string;
}

interface RequestActionsMenuProps {
  request: BookRequest;
  activeTab: 'sent' | 'received';
  onActionComplete: () => void;
  onOpenRatingModal?: (request: BookRequest) => void;
  onOpenCancelModal?: (request: BookRequest) => void;
}

const RequestActionsMenu: React.FC<RequestActionsMenuProps> = ({
  request,
  activeTab,
  onActionComplete,
  onOpenRatingModal,
  onOpenCancelModal
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loadingActions, setLoadingActions] = useState<{[key: string]: boolean}>({});
  const { showSuccess, showError } = useToast();

  const handleAction = async (action: string, actionFn: () => Promise<void>) => {
    const actionKey = `${action}-${request.id}`;
    setLoadingActions(prev => ({ ...prev, [actionKey]: true }));
    
    try {
      await actionFn();
      onActionComplete();
      setIsOpen(false);
    } catch (error: any) {
      if (error.message === 'Request timeout') {
        showError('Erreur', 'L\'action a pris trop de temps. Vérifiez vos demandes pour voir si elle a été traitée.');
      } else {
        showError('Erreur', error.response?.data?.error || 'Erreur lors de l\'action');
      }
    } finally {
      setLoadingActions(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const respondToRequest = async (status: 'accepted' | 'rejected') => {
    await handleAction('respond', async () => {
      await axios.patch(`/api/users/request/${request.id}`, { status });
      showSuccess('Succès', `Demande ${status === 'accepted' ? 'acceptée' : 'refusée'} avec succès`);
    });
  };

  const markAsReturned = async () => {
    await handleAction('return', async () => {
      await axios.patch(`/api/users/return-book/${request.book_id}`);
      showSuccess('Succès', 'Livre marqué comme retourné');
    });
  };

  const cancelRequest = async () => {
    if (onOpenCancelModal) {
      onOpenCancelModal(request);
      setIsOpen(false);
    }
  };

  const openRating = () => {
    if (onOpenRatingModal) {
      onOpenRatingModal(request);
      setIsOpen(false);
    }
  };

  const getAvailableActions = () => {
    const actions = [];

    if (activeTab === 'received') {
      if (request.status === 'pending') {
        actions.push(
          {
            label: 'Accepter',
            icon: Check,
            onClick: () => respondToRequest('accepted'),
            className: 'text-green-700 hover:bg-green-50',
            loadingKey: `respond-${request.id}`
          },
          {
            label: 'Refuser',
            icon: X,
            onClick: () => respondToRequest('rejected'),
            className: 'text-red-700 hover:bg-red-50',
            loadingKey: `respond-${request.id}`
          }
        );
      } else if (request.status === 'accepted') {
        actions.push({
          label: 'Marquer comme retourné',
          icon: RotateCcw,
          onClick: markAsReturned,
          className: 'text-forest-700 hover:bg-forest-50',
          loadingKey: `return-${request.id}`
        });
      }
    } else if (activeTab === 'sent') {
      if (request.status === 'pending') {
        actions.push({
          label: 'Annuler la demande',
          icon: X,
          onClick: cancelRequest,
          className: 'text-red-700 hover:bg-red-50'
        });
      }
    }

    // Actions communes
    if (request.status === 'completed' && request.can_rate) {
      actions.push({
        label: 'Noter l\'échange',
        icon: Star,
        onClick: openRating,
        className: 'text-yellow-700 hover:bg-yellow-50'
      });
    }

    return actions;
  };

  const availableActions = getAvailableActions();

  if (availableActions.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        title="Actions"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {isOpen && (
        <>
          {/* Overlay pour fermer le menu */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu déroulant */}
          <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
            <div className="py-2">
              {availableActions.map((action, index) => {
                const Icon = action.icon;
                const isLoading = loadingActions[action.loadingKey || ''] || false;
                
                return (
                  <button
                    key={index}
                    onClick={action.onClick}
                    disabled={isLoading}
                    className={`w-full flex items-center px-4 py-3 text-xs font-medium transition-all duration-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed border-b border-gray-100 last:border-b-0 ${action.className}`}
                  >
                    {isLoading ? (
                      <Loader2 className="h-3 w-3 mr-2 animate-spin flex-shrink-0" />
                    ) : (
                      <Icon className="h-3 w-3 mr-2 flex-shrink-0" />
                    )}
                    <span className="whitespace-nowrap">{action.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RequestActionsMenu;
